import React, { FC, useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ClaimType } from '@yakoffice/publisher-types';
import RootStoreContext from '../../../model/RootStoreContext';
import { Link } from 'react-router-dom';
import { useExperimentsResult } from '../../../loaders/useExperiments';
import { CustomSpinner } from '@yakoffice/custom-spinner';
import { AbbreviatedString } from '../../components/abbreviated-string/AbbreviatedString';
import { FormatPropertyValue } from '../common/PropertyValueDisplay';
import { useGetEntityLink, useGetKindLink } from '../../routes';
import { YoPublisherTable, YoTableClassNames } from '../../components/yo-publisher-table/YoPublisherTable';
import { TdFlexColumn } from '../../components/td-flex-column/TdFlexColumn';
import Accordion from 'react-bootstrap/cjs/Accordion';
import { SpecificationProperties } from '../common/SpecificationProperties';
import { StatusLabel } from '../../components/tables/status-label/StatusLabel';
import { IExperiment } from '../../../model/experiment/Experiment';

interface PropsType {
    experimentsResult: useExperimentsResult;
    handleCreateExperiment: () => void;
    experimentNameSearchValue: string;
}

export const ExperimentsTable: FC<PropsType> = observer((props) => {

    const rootStore = useContext(RootStoreContext);
    const getKindLink = useGetKindLink();
    const getEntityLink = useGetEntityLink();
    const [accoridonActiveKey, setAccordionActiveKey] = useState(0);

    const experimentRow = (experiment: IExperiment) => {

        return (
            <tr key={experiment.id} className={`${experiment.status}`} data-testid={experiment.name}>
                <td className="text-center td-status">
                    <StatusLabel status={experiment.status} />
                </td>
                <td data-testid="tdExperimentName">
                    {rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditExperiments) ? (
                        <span className="text-info cursor-pointer">
                            <Link className="w-75 d-inline-block" to={experiment.id.toString()}>
                                {experiment.name}
                            </Link>
                        </span>
                    ) : (
                        experiment.name
                    )}
                </td>
                <td style={{ height: '4rem' }} data-testid="tdExperimentBounds">
                    {experiment.lowerBound} to {experiment.upperBound}
                    {/*<UserRange lowerBound={experiment.lowerBound} upperBound={experiment.upperBound}*/}
                    {/*           displayValueMarks={true}*/}
                    {/*           disabled={true} />*/}
                </td>
                <td style={{ height: '4rem' }} data-testid="tdVariants">
                    {/*<div className="h-100 d-flex flex-column justify-content-between">*/}
                    {experiment.variants
                        .slice()
                        .sort((v1, v2) => v1.id - v2.id)
                        .map((variant) => (
                            <div key={variant.id}>
                                {variant.name}@{variant.allocation}
                            </div>
                        ))}
                    {/*</div>*/}
                </td>
                <TdFlexColumn flexColClassName="justify-content-around align-items-left">
                    {experiment.properties.map((p) => (
                        <div key={p.id}>
                            <Link className="text-info"
                                  to={getKindLink({ categoryId: p.kindCategoryId, kindId: p.kindId })}>
                                {p.kindName}
                            </Link>
                        </div>
                    ))}
                </TdFlexColumn>
                <TdFlexColumn flexColClassName="justify-content-around align-items-left">
                    {experiment.properties.map((p) => (
                        <div key={p.id}>
                            <Link
                                className="text-info"
                                to={getEntityLink({
                                    categoryId: p.kindCategoryId,
                                    kindId: p.kindId,
                                    entityId: p.entityId
                                })}
                            >
                                {p.entityName}
                            </Link>
                        </div>
                    ))}
                </TdFlexColumn>
                <TdFlexColumn flexColClassName="justify-content-around align-items-left">
                    {experiment.properties.map((p) => (
                        <div key={p.id}>{p.kindPropertyKey}</div>
                    ))}
                </TdFlexColumn>
                <TdFlexColumn flexColClassName="justify-content-around text-end">
                    {experiment.properties.map((p) => (
                        <div key={p.id}>
                            <AbbreviatedString
                                value={`${FormatPropertyValue(rootStore, p.kindPropertyType, p.kindPropertySelectionId, p.kindPropertyEntitySelectionKindId, p.entityPropertyValue)}`} />
                        </div>
                    ))}
                </TdFlexColumn>
                <TdFlexColumn flexColClassName="align-items-left">
                    <Accordion activeKey={accoridonActiveKey.toString()}>
                        <div
                            onClick={() => setAccordionActiveKey(experiment.id === accoridonActiveKey ? 0 : experiment.id)}
                            className="text-info cursor-pointer">
                            {experiment.id === accoridonActiveKey && (
                                <div>
                                    Hide <i className="fa-solid fa-caret-up" />
                                </div>
                            )}
                            {experiment.id !== accoridonActiveKey && (
                                <div>
                                    Hide <i className="fa-solid fa-caret-down" />
                                </div>
                            )}
                        </div>
                        <Accordion.Collapse eventKey={experiment.id.toString()}>
                            <div>
                                {experiment.properties.map((p) => (
                                    <div key={p.id} className="border-bottom mb-1">
                                        {p.values
                                            .slice()
                                            .sort((v1, v2) => v1.variantId - v2.variantId)
                                            .map((variantValue) => (
                                                <div key={variantValue.id}>
                                                    <>
                                                        {experiment.variants.find((v) => v.id === variantValue.variantId)?.name}:{' '}
                                                        <AbbreviatedString
                                                            value={`${FormatPropertyValue(
                                                                rootStore,
                                                                p.kindPropertyType,
                                                                p.kindPropertySelectionId,
                                                                p.kindPropertyEntitySelectionKindId,
                                                                variantValue.value
                                                            )}`}
                                                        />
                                                    </>
                                                </div>
                                            ))}
                                    </div>
                                ))}
                            </div>
                        </Accordion.Collapse>
                    </Accordion>
                </TdFlexColumn>
                <td>
                    <SpecificationProperties entityProperties={experiment.specifications}
                                             specificationKindVersions={rootStore.specificationsForExperimentsStore.getKinds()} />
                </td>
                <td className="td-distributed">
                    {experiment.inLatestDistribution && <div>Yes (in latest)</div>}
                    {!experiment.inLatestDistribution && experiment.inAnyDistribution && <div>Yes (in previous)</div>}
                    {!experiment.inLatestDistribution && !experiment.inAnyDistribution && <div>No</div>}
                </td>
            </tr>
        );
    };

    const renderExperiments = (experiments: IExperiment[]) => {

        return props.experimentNameSearchValue !== '' ?
            experiments.filter(experiment => experiment.name.toLocaleLowerCase().includes(props.experimentNameSearchValue.toLocaleLowerCase())).map(experiment => experimentRow(experiment))
            :
            experiments.map(experiment => experimentRow(experiment));
    };

    return (
        <div className="mdcDataTableContainer">
            <YoPublisherTable
                className={`${YoTableClassNames.MinWidthContainer}, ${YoTableClassNames.AlignTop}`}
                data-testid="tblExperiments">
                <thead>
                <tr>
                    <th className="td-status">Status</th>
                    <th>Name</th>
                    <th>User Range</th>
                    <th>Variants</th>
                    <th>Kind</th>
                    <th>Entity</th>
                    <th>Property</th>
                    <th>Control Value</th>
                    <th>Variant Values</th>
                    <th>Specifications</th>
                    <th className="td-distributed">Distributed</th>
                </tr>
                </thead>
                <tbody>
                {!props.experimentsResult.isLoaded &&
                    <tr>
                        <td colSpan={42}><CustomSpinner spinnerText="Loading Experiments..." /></td>
                    </tr>
                }
                {props.experimentsResult.isLoaded && props.experimentsResult.experiments && props.experimentsResult.experiments.length === 0 &&
                    <tr>
                        <td colSpan={42} className="text-center">No Experiments found for this Game Version and
                            Environment with current filters
                        </td>
                    </tr>
                }
                {
                    props.experimentsResult.isLoaded && props.experimentsResult.experiments && props.experimentsResult.experiments.length > 0 &&
                    renderExperiments(props.experimentsResult.experiments)
                }
                </tbody>
            </YoPublisherTable>
        </div>
    );
});
